import { ref, reactive, computed } from 'vue'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface Toast {
  id: string
  type: ToastType
  title: string
  message?: string
  duration?: number
  persistent?: boolean
}

interface ToastState {
  toasts: Toast[]
}

const state = reactive<ToastState>({
  toasts: []
})

let toastId = 0

/**
 * Composable para gestión de notificaciones toast
 * Proporciona un sistema centralizado de notificaciones con auto-dismiss
 */
export function useToast() {
  
  // ========== GETTERS ==========
  
  const toasts = computed(() => state.toasts)
  const hasToasts = computed(() => state.toasts.length > 0)
  
  // ========== MÉTODOS ==========
  
  /**
   * Genera un ID único para el toast
   */
  const generateId = () => `toast-${++toastId}-${Date.now()}`
  
  /**
   * Añade un toast al stack
   */
  const addToast = (toast: Omit<Toast, 'id'>): string => {
    const id = generateId()
    const newToast: Toast = {
      id,
      duration: 5000, // 5 segundos por defecto
      ...toast
    }
    
    state.toasts.push(newToast)
    
    // Auto-dismiss si no es persistente
    if (!newToast.persistent && newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        removeToast(id)
      }, newToast.duration)
    }
    
    return id
  }
  
  /**
   * Elimina un toast por ID
   */
  const removeToast = (id: string) => {
    const index = state.toasts.findIndex(toast => toast.id === id)
    if (index > -1) {
      state.toasts.splice(index, 1)
    }
  }
  
  /**
   * Limpia todos los toasts
   */
  const clearAll = () => {
    state.toasts.splice(0)
  }
  
  // ========== MÉTODOS DE CONVENIENCIA ==========
  
  /**
   * Toast de éxito
   */
  const success = (title: string, message?: string, options?: Partial<Toast>) => {
    return addToast({
      type: 'success',
      title,
      message,
      ...options
    })
  }
  
  /**
   * Toast de error
   */
  const error = (title: string, message?: string, options?: Partial<Toast>) => {
    return addToast({
      type: 'error',
      title,
      message,
      duration: 7000, // Los errores duran más
      ...options
    })
  }
  
  /**
   * Toast de advertencia
   */
  const warning = (title: string, message?: string, options?: Partial<Toast>) => {
    return addToast({
      type: 'warning',
      title,
      message,
      duration: 6000,
      ...options
    })
  }
  
  /**
   * Toast informativo
   */
  const info = (title: string, message?: string, options?: Partial<Toast>) => {
    return addToast({
      type: 'info',
      title,
      message,
      ...options
    })
  }
  
  /**
   * Toast para operaciones exitosas de familia/miembro
   */
  const familySuccess = (action: string, familyName?: string) => {
    const messages = {
      created: `Familia "${familyName}" creada exitosamente`,
      updated: `Familia "${familyName}" actualizada correctamente`,
      deleted: `Familia "${familyName}" eliminada correctamente`,
      memberAdded: `Miembro agregado a "${familyName}" exitosamente`,
      memberUpdated: `Miembro actualizado correctamente`,
      memberDeleted: `Miembro eliminado correctamente`
    }
    
    return success('¡Operación exitosa!', messages[action as keyof typeof messages] || action)
  }
  
  /**
   * Toast para errores de familia/miembro
   */
  const familyError = (action: string, errorMessage?: string) => {
    const baseMessages = {
      create: 'Error al crear la familia',
      update: 'Error al actualizar la familia',
      delete: 'Error al eliminar la familia',
      load: 'Error al cargar las familias',
      memberAdd: 'Error al agregar el miembro',
      memberUpdate: 'Error al actualizar el miembro',
      memberDelete: 'Error al eliminar el miembro'
    }
    
    const title = baseMessages[action as keyof typeof baseMessages] || 'Error en operación'
    return error(title, errorMessage || 'Inténtalo de nuevo más tarde')
  }
  
  return {
    // Estado
    toasts,
    hasToasts,
    
    // Métodos generales
    addToast,
    removeToast,
    clearAll,
    
    // Métodos de conveniencia
    success,
    error,
    warning,
    info,
    
    // Métodos específicos para familias
    familySuccess,
    familyError
  }
}